import React, { useState, useEffect, useCallback, useRef } from "react";
import { wasteApi } from "@/api/waste";
import { Trash2, X, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

function makeClientId() {
  return `cli_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * WasteMicroForm — Modal ultra-ràpid pel WorkerApp.
 * Només 3 camps: producte (amb autocomplete), quantitat (+unitat) i motiu.
 * Optimitzat pel mòbil i ús ràpid (<5s) sense entrar al dashboard.
 */
export default function WasteMicroForm({ venueId, onClose }) {
  const [step, setStep] = useState("form"); // form | success
  const [productName, setProductName] = useState("");
  const [ingredientId, setIngredientId] = useState(null);
  const [unit, setUnit] = useState("kg");
  const [unitCost, setUnitCost] = useState(null);
  const [quantity, setQuantity] = useState("");
  const [reason, setReason] = useState("caducitat");
  const [reasons, setReasons] = useState([
    { code: "caducitat", label: "Caducitat" },
    { code: "mal_estat", label: "Mal estat" },
    { code: "sobreproduccio", label: "Sobreproducció" },
    { code: "error_prep", label: "Error preparació" },
    { code: "vessament", label: "Vessament" },
    { code: "altres", label: "Altres" },
  ]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [saving, setSaving] = useState(false);
  const debRef = useRef(null);

  // Load settings (per a personalització de motius i unitats si calen)
  useEffect(() => {
    if (!venueId) return;
    wasteApi.getSettings(venueId).then((s) => {
      if (s?.waste_reasons?.length) setReasons(s.waste_reasons);
    }).catch(() => { /* fallback to defaults */ });
  }, [venueId]);

  const fetchSuggest = useCallback(async (q) => {
    if (!venueId) return;
    try {
      const items = await wasteApi.productSuggestions(venueId, q);
      setSuggestions(items || []);
    } catch (e) { /* noop */ }
  }, [venueId]);

  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => fetchSuggest(productName), 200);
    return () => debRef.current && clearTimeout(debRef.current);
  }, [productName, fetchSuggest]);

  const pickSuggestion = (s) => {
    setProductName(s.name);
    setShowSuggest(false);
    if (s.source === "ingredient") {
      setIngredientId(s.ingredient_id);
      if (s.unit) setUnit(s.unit);
      if (s.cost_per_unit != null) setUnitCost(s.cost_per_unit);
    } else {
      setIngredientId(null);
      setUnitCost(null);
    }
  };

  const submit = async () => {
    if (!productName.trim()) { toast.error("Indica el producte"); return; }
    const q = parseFloat(quantity);
    if (!q || q <= 0) { toast.error("Quantitat invàlida"); return; }
    setSaving(true);
    try {
      await wasteApi.createRecord({
        venue_id: venueId,
        client_id: makeClientId(),
        product_name: productName.trim(),
        ingredient_id: ingredientId,
        quantity: q,
        unit,
        unit_cost: unitCost,
        reason,
        zone: "cuina",
        family: "altres",
      });
      setStep("success");
      // Auto-close després de 1.5s
      setTimeout(() => onClose && onClose(true), 1500);
    } catch (err) {
      if (!navigator.onLine) {
        toast.info("Sense connexió — guardat per sincronitzar");
        setStep("success");
        setTimeout(() => onClose && onClose(true), 1500);
      } else {
        toast.error(err.message || "Error guardant");
      }
    } finally {
      setSaving(false);
    }
  };

  const units = ["kg", "g", "l", "ml", "u"];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="waste-micro-modal">
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl shadow-2xl max-h-[90vh] overflow-y-auto">
        {step === "success" ? (
          <div className="p-8 text-center" data-testid="waste-micro-success">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <Check className="w-8 h-8 text-emerald-600" />
            </div>
            <p className="text-xl font-bold text-gray-900 mb-1">Merma registrada</p>
            <p className="text-sm text-gray-500">Gràcies pel registre!</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between px-5 pt-5 pb-2">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-[#7B2D3F]/10 flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-[#7B2D3F]" />
                </div>
                <div>
                  <p className="font-bold text-gray-900">Registrar merma</p>
                  <p className="text-xs text-gray-500">Ràpid · només 3 camps</p>
                </div>
              </div>
              <button onClick={() => onClose && onClose(false)} className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center" data-testid="waste-micro-close">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Producte */}
              <div className="relative">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Producte</label>
                <input
                  data-testid="waste-micro-product"
                  type="text"
                  value={productName}
                  onChange={(e) => { setProductName(e.target.value); setShowSuggest(true); setIngredientId(null); setUnitCost(null); }}
                  onFocus={() => setShowSuggest(true)}
                  onBlur={() => setTimeout(() => setShowSuggest(false), 200)}
                  placeholder="Ex: Pa, Tomàquet…"
                  autoComplete="off"
                  className="w-full mt-1 px-4 py-3 text-base border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#7B2D3F]/30"
                />
                {showSuggest && suggestions.length > 0 && (
                  <div className="absolute left-0 right-0 mt-1 bg-white rounded-xl border shadow-lg max-h-48 overflow-y-auto z-10">
                    {suggestions.map((s, i) => (
                      <button
                        type="button"
                        key={`${s.source}-${s.name}-${i}`}
                        onClick={() => pickSuggestion(s)}
                        className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center justify-between text-sm border-b last:border-b-0"
                        data-testid={`waste-micro-sugg-${i}`}
                      >
                        <span className="font-medium">{s.name}</span>
                        {s.source === "ingredient" && s.cost_per_unit != null && (
                          <span className="text-xs text-emerald-700">{s.cost_per_unit} €/{s.unit}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Quantitat + Unitat */}
              <div>
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Quantitat</label>
                <div className="mt-1 flex gap-2">
                  <input
                    data-testid="waste-micro-qty"
                    type="number" min="0" step="0.01" inputMode="decimal"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    placeholder="0.00"
                    className="flex-1 px-4 py-3 text-lg font-semibold border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#7B2D3F]/30"
                  />
                  <div className="flex gap-1 bg-gray-100 rounded-xl p-1" data-testid="waste-micro-units">
                    {units.map((u) => (
                      <button
                        key={u} type="button"
                        onClick={() => setUnit(u)}
                        className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                          unit === u ? "bg-[#7B2D3F] text-white" : "text-gray-600 hover:bg-gray-200"
                        }`}
                      >{u}</button>
                    ))}
                  </div>
                </div>
                {unitCost != null && quantity && parseFloat(quantity) > 0 && (
                  <p className="text-xs text-gray-500 mt-2">
                    Cost estimat: <strong className="text-[#7B2D3F]">{(parseFloat(quantity) * unitCost).toFixed(2)} €</strong>
                  </p>
                )}
              </div>

              {/* Motiu */}
              <div>
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Motiu</label>
                <div className="mt-1 grid grid-cols-2 gap-2" data-testid="waste-micro-reasons">
                  {reasons.map((r) => (
                    <button
                      key={r.code} type="button"
                      onClick={() => setReason(r.code)}
                      className={`px-3 py-2.5 rounded-xl text-sm font-medium border-2 transition-colors text-left ${
                        reason === r.code
                          ? "border-[#7B2D3F] bg-[#7B2D3F]/5 text-[#7B2D3F]"
                          : "border-gray-200 text-gray-700 hover:border-gray-300"
                      }`}
                      data-testid={`waste-micro-reason-${r.code}`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Submit */}
              <button
                onClick={submit}
                disabled={saving}
                className="w-full py-4 bg-[#7B2D3F] hover:bg-[#5B1D2F] disabled:opacity-50 text-white text-lg font-bold rounded-2xl shadow-lg shadow-[#7B2D3F]/30 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                data-testid="waste-micro-submit"
              >
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Trash2 className="w-5 h-5" />}
                Registrar
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
